<template lang="pug">
list-view(
  :items='featuredStickers'
  :loading='loading'
  :myCMSHomeScreenData='myCMSHomeScreenData'
  @order-changed='handleOrderChanged'
  @delete-item='handleDelete')
  template(#action)
    el-button(type='success' color="#303133"  @click='dialog = true' v-bind:disabled = 'loading')
        img(src="@/assets/icons/plusWhite.svg"
        style="width: 35px; height: 20px; margin-right: 5px;").me-1.pe-2
        | Add
  template(#dialog)
    dialog-add-to-featured-items(
      v-model="dialog"
      title="SEARCH STICKER"
      :items='items'
      :featuredItems='featuredStickers'
      :loading='loading'
      @save-item="saveItem"
      @close-dialog="dialog = false"
      @page-change="handlePageChange"
      @items-per-page-change="handleItemsPerPageChange"
      @search="handleSearch"
    )
</template>

<script>
import {
  defineComponent, reactive, onMounted, toRef, computed, watch,
} from 'vue';
import shared from '../../shared';

// import { stickers } from '@/data/static-home-page';

import ListView from './ListView.vue';
import DialogAddToFeaturedItems from './DialogAddToFeaturedItems.vue';

export default defineComponent({
  name: 'HomePage_Spots',
  props: {
    myCMSHomeScreenData: { type: Object, default: () => ({}) },
  },
  components: {
    DialogAddToFeaturedItems,
    ListView,
  },

  setup(props) {
    const {
      getStickers,
      saveFeaturedSticker,
      fetchCMSHomeScreenData,
    } = shared();
    const state = reactive({
      items: [],
      dialog: false,
      loading: true,
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      searchQuery: '',
      featuredStickers: [],
    });

    // Create a computed property for featured stickers to avoid direct prop mutations
    const featuredStickers = computed(() => props.myCMSHomeScreenData.metaFeaturedStickers || []);

    // Update local state when props change
    watch(() => props.myCMSHomeScreenData.metaFeaturedStickers, (newVal) => {
      if (newVal) {
        state.featuredStickers = [...newVal];
      }
    }, { immediate: true, deep: true });

    async function getItems() {
      try {
        const offset = (state.currentPage - 1) * state.itemsPerPage;
        const stickers = await getStickers(
          state.itemsPerPage,
          offset,
          state.searchQuery,
          ['Active'],
          'CreatedAt',
          false,
        );

        if (stickers != null) {
          state.totalItems = stickers.totalCount || stickers.length;
          const mStickers = stickers.items ? stickers.items.map((x) => ({
            id: x.id,
            title: x.title,
            image: x.url,
            order: x.sortOrder,
            featured: false,
          })) : stickers.map((x) => ({
            id: x.id,
            title: x.title,
            image: x.url,
            order: x.sortOrder,
            featured: false,
          }));
          return mStickers;
        }
        state.totalItems = 0;
        return [];
      } catch (error) {
        console.error('Error fetching stickers:', error);
        state.totalItems = 0;
        return [];
      }
    }

    const handleDelete = async (item) => {
      try {
        state.loading = true;
        await saveFeaturedSticker({
          ...item,
          featured: false,
        });

        // Update local state instead of modifying props directly
        state.featuredStickers = state.featuredStickers.filter(
          (sticker) => sticker.id !== item.id,
        );
        // Update the myCMSHomeScreenData object with the new array
        if (props.myCMSHomeScreenData) {
          const updatedData = { ...props.myCMSHomeScreenData };
          updatedData.metaFeaturedStickers = state.featuredStickers;
          Object.assign(props.myCMSHomeScreenData, updatedData);
        }
      } catch (error) {
        console.error('Error removing featured sticker:', error);
      } finally {
        state.loading = false;
      }
    };

    const handleOrderChanged = async (items) => {
      try {
        state.loading = true;

        const stickersToSave = items.map((item) => ({
          id: item.id,
          title: item.title,
          image: item.image,
          order: item.order,
          featured: true,
        }));

        const result = await saveFeaturedSticker(stickersToSave);

        if (result?.metaFeaturedStickers) {
          const sortedStickers = result.metaFeaturedStickers.sort((a, b) => a.order - b.order);
          // Update local state
          state.featuredStickers = sortedStickers;
          // Update the myCMSHomeScreenData object
          if (props.myCMSHomeScreenData) {
            const updatedData = { ...props.myCMSHomeScreenData };
            updatedData.metaFeaturedStickers = sortedStickers;
            Object.assign(props.myCMSHomeScreenData, updatedData);
          }
        }
      } catch (error) {
        console.error('Error saving order changes:', error);
      } finally {
        state.loading = false;
      }
    };

    const saveItem = async (item) => {
      try {
        state.loading = true;
        await saveFeaturedSticker(item);

        const response = await fetchCMSHomeScreenData();
        if (response?.metaFeaturedStickers) {
          const uniqueStickers = response.metaFeaturedStickers.reduce((acc, sticker) => {
            const existingIndex = acc.findIndex((s) => s.id === sticker.id);
            if (existingIndex !== -1) {
              acc[existingIndex] = sticker;
            } else {
              acc.push(sticker);
            }
            return acc;
          }, []);

          const sortedStickers = uniqueStickers.sort((a, b) => a.order - b.order);
          // Update local state
          state.featuredStickers = sortedStickers;
          // Update the myCMSHomeScreenData object
          if (props.myCMSHomeScreenData) {
            const updatedData = { ...props.myCMSHomeScreenData };
            updatedData.metaFeaturedStickers = sortedStickers;
            Object.assign(props.myCMSHomeScreenData, updatedData);
          }
        }
      } catch (error) {
        console.error('Error saving featured sticker:', error);
      } finally {
        state.loading = false;
      }
    };

    const handlePageChange = async (page) => {
      try {
        state.currentPage = page;
        state.loading = true;
        state.items = await getItems();
        state.loading = false;
      } catch (error) {
        console.error('Error changing page:', error);
      }
    };

    const handleItemsPerPageChange = async (itemsPerPage) => {
      try {
        state.itemsPerPage = itemsPerPage;
        state.currentPage = 1;
        state.loading = true;
        state.items = await getItems();
        state.loading = false;
      } catch (error) {
        console.error('Error changing items per page:', error);
      }
    };

    const handleSearch = async (query) => {
      try {
        state.searchQuery = query;
        state.currentPage = 1;
        state.loading = true;
        state.items = await getItems();
        state.loading = false;
      } catch (error) {
        console.error('Error searching stickers:', error);
      }
    };

    onMounted(async () => {
      try {
        state.loading = true;
        state.items = await getItems();
      } catch (error) {
        console.error('Error initializing component:', error);
      } finally {
        state.loading = false;
      }
    });

    return {
      items: toRef(state, 'items'),
      dialog: toRef(state, 'dialog'),
      loading: toRef(state, 'loading'),
      totalItems: toRef(state, 'totalItems'),
      featuredStickers,
      handleDelete,
      saveItem,
      handlePageChange,
      handleItemsPerPageChange,
      handleSearch,
      handleOrderChanged,
    };
  },
});

</script>
